import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useBreadcrumbStore, type BreadcrumbItem } from '@/store/breadCrumb'
import { pinia } from '@/config/pinia'

type BreadcrumbContext = {
  to: RouteLocationNormalized
  from: RouteLocationNormalized
}

type ValueOrFactory<T> = T | ((context: BreadcrumbContext) => T)

interface BreadcrumbDefinition {
  label: ValueOrFactory<string>
  route?: ValueOrFactory<string | undefined>
  parent?: ValueOrFactory<string | undefined | null>
}

const getParam = (
  route: RouteLocationNormalized,
  key: string
): string | undefined => {
  const value = route.params[key]
  if (Array.isArray(value)) return value[0]
  return (value as string) ?? undefined
}

const isKnownRoute = (name?: string | null): name is string =>
  !!name && name in BREADCRUMB_DEFINITIONS

const deriveParentFromContext =
  (fallback?: string) =>
  ({ to, from }: BreadcrumbContext) => {
    const fromName = from?.name as string | undefined
    if (
      fromName &&
      fromName !== (to.name as string | undefined) &&
      isKnownRoute(fromName)
    ) {
      return fromName
    }
    return fallback
  }

const BREADCRUMB_DEFINITIONS: Record<string, BreadcrumbDefinition> = {
  inicio: {
    label: 'Inicio',
    route: '/home',
  },
  payrollsConfig: {
    label: 'Configuración de nómina',
    route: '/payrollsConfig',
    parent: 'inicio',
  },
  'payrollsConfig/detail': {
    label: 'Detalles del grupo de nóminas',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}` : undefined
    },
    parent: 'payrollsConfig',
  },
  'payrollsConfig/add': {
    label: 'Crear grupo de nóminas',
    route: '/payrollsConfig/add',
    parent: 'payrollsConfig',
  },
  approvalFlows: {
    label: 'Flujos de aprobación',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}/approvalFlows` : undefined
    },
    parent: 'payrollsConfig/detail',
  },
  CreateApprovalFlow: {
    label: 'Crear flujo de aprobación',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}/approvalFlows/add` : undefined
    },
    parent: 'approvalFlows',
  },
  ApprovalFlowDetail: {
    label: 'Detalle flujo de aprobación',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      const flowId = getParam(to, 'flowId')
      return id && flowId
        ? `/payrollsConfig/${id}/approvalFlows/detail/${flowId}`
        : undefined
    },
    parent: 'approvalFlows',
  },
  documentGroups: {
    label: 'Grupos de documentos',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}/documentGroups` : undefined
    },
    parent: 'payrollsConfig/detail',
  },
  createDocumentGroup: {
    label: 'Crear grupo de documentos',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}/documentGroups/add` : undefined
    },
    parent: 'documentGroups',
  },
  documentGroupDetail: {
    label: 'Detalle grupo de documentos',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      const documentGroupId = getParam(to, 'documentGroupId')
      return id && documentGroupId
        ? `/payrollsConfig/${id}/documentGroups/detail/${documentGroupId}`
        : undefined
    },
    parent: 'documentGroups',
  },
  calendarGroups: {
    label: 'Grupos de calendarios',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}/calendarGroups` : undefined
    },
    parent: 'payrollsConfig/detail',
  },
  createCalendarGroup: {
    label: 'Crear grupo de calendarios',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}/calendarGroups/add` : undefined
    },
    parent: 'calendarGroups',
  },
  calendarGroupDetail: {
    label: 'Detalle grupo de calendarios',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      const calendarGroupId = getParam(to, 'calendarGroupId')
      return id && calendarGroupId
        ? `/payrollsConfig/${id}/calendarGroups/detail/${calendarGroupId}`
        : undefined
    },
    parent: 'calendarGroups',
  },
  calendarPeriods: {
    label: 'Grupo de periodos de calendarios',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}/calendarPeriods` : undefined
    },
    parent: 'payrollsConfig/detail',
  },
  'calendarPeriods/add': {
    label: 'Crear grupo de periodos de calendario',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/payrollsConfig/${id}/calendarPeriods/add` : undefined
    },
    parent: 'calendarPeriods',
  },
  'calendarPeriods/detail': {
    label: 'Detalles de periodos de calendario',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      const calendarPeriodId = getParam(to, 'calendarPeriodId')
      return id && calendarPeriodId
        ? `/payrollsConfig/${id}/calendarPeriods/detail/${calendarPeriodId}`
        : undefined
    },
    parent: 'calendarPeriods',
  },
  clients: {
    label: 'Clientes',
    route: '/clients',
    parent: 'inicio',
  },
  'client/add': {
    label: 'Crear cliente',
    route: '/clients/add',
    parent: 'clients',
  },
  'clients/detail': {
    label: 'Detalles del cliente',
    route: ({ to }) => {
      const clientId = getParam(to, 'id')
      return clientId ? `/clients/${clientId}` : undefined
    },
    parent: 'clients',
  },
  companies: {
    label: 'Compañias',
    route: '/companies',
    parent: 'inicio',
  },
  'companiesa/add': {
    label: 'Crear compañia',
    route: '/companies/add',
    parent: 'companies',
  },
  users: {
    label: 'Usuarios',
    route: '/users',
    parent: 'inicio',
  },
  'users/add': {
    label: 'Crear usuario',
    route: '/users/add',
    parent: 'users',
  },
  'users/detail': {
    label: 'Detalles del usuario',
    route: ({ to }) => {
      const username = getParam(to, 'username')
      return username ? `/users/${username}` : undefined
    },
    parent: 'users',
  },
  approvers: {
    label: 'Aprobadores',
    route: '/approvers',
    parent: 'inicio',
  },
  employees: {
    label: 'Empleados',
    route: '/employees',
    parent: 'inicio',
  },
  Reports: {
    label: 'Reportes',
    route: '/reports',
    parent: 'inicio',
  },
  VacationReport: {
    label: 'Reporte de vacaciones',
    route: '/reports/vacations',
    parent: 'Reports',
  },
  lettersReports: {
    label: 'Reportes de cartas',
    route: '/reports/letters',
    parent: 'letterConfigs',
  },
  letterConfigs: {
    label: 'Configuraciones de cartas',
    route: '/letterConfigs',
    parent: 'inicio',
  },
  CreateLetterConfig: {
    label: 'Crear configuración de carta',
    route: '/letterConfig/add',
    parent: 'letterConfigs',
  },
  LetterConfigDetail: {
    label: 'Detalles de configuración de carta',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/letterConfig/detail/${id}` : undefined
    },
    parent: 'letterConfigs',
  },
  CreateExternalLetters: {
    label: 'Crear carta externa',
    route: '/letterExternals/create',
    parent: deriveParentFromContext('letterConfigs'),
  },
  ExternalLetterDetail: {
    label: 'Detalles de cartas externa',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/letterExternals/detail/${id}` : undefined
    },
    parent: deriveParentFromContext('letterConfigs'),
  },
  hr: {
    label: 'Gestión de Empleados',
    route: '/hr',
    parent: 'inicio',
  },
  'hr-letters': {
    label: 'Cartas Externas',
    route: '/Externalletters',
    parent: 'inicio',
  },
  profile: {
    label: 'Perfil',
    route: '/profile',
    parent: 'inicio',
  },
  receipts: {
    label: 'Recibos de nómina',
    route: '/receipts',
    parent: 'inicio',
  },
  Vacations: {
    label: 'Vacaciones',
    route: '/vacations',
    parent: 'inicio',
  },
  Licenses: {
    label: 'Licencias',
    route: '/licenses',
    parent: 'inicio',
  },
  'Documents & Policies': {
    label: 'Documentos y políticas',
    route: '/documents&Policies',
    parent: 'inicio',
  },
  LetterRequests: {
    label: 'Cartas y constancias',
    route: '/letterRequests',
    parent: 'inicio',
  },
  LetterRequestsApprover: {
    label: 'Aprobaciones de cartas',
    route: '/letterRequests/approver',
    parent: 'inicio',
  },
  LetterRequestsdetail: {
    label: 'Detalle de solicitud',
    route: ({ to }) => {
      const id = getParam(to, 'id')
      return id ? `/letterRequests/${id}` : undefined
    },
    parent: 'LetterRequests',
  },
  RequestHistory: {
    label: 'Historial de solicitudes',
    route: '/requestHistory',
    parent: 'inicio',
  },
  RequestDetail: {
    label: 'Detalles de solicitud',
    route: ({ to }) => {
      const requestId = getParam(to, 'id')
      const origin = getParam(to, 'from')
      return requestId
        ? `/requestDetail/${requestId}/from/${origin ?? ''}`
        : undefined
    },
    parent: ({ to }) => {
      const origin = getParam(to, 'from')
      return origin === 'requestApprover' ? 'RequestApprover' : 'RequestHistory'
    },
  },
  RequestApprover: {
    label: 'Aprobador de solicitudes',
    route: '/requestApprover',
    parent: 'inicio',
  },
  RequestApproverDetail: {
    label: 'Detalles de solicitud',
    route: ({ to }) => {
      const requestId = getParam(to, 'id')
      return requestId ? `/requestApprover/${requestId}` : undefined
    },
    parent: 'RequestApprover',
  },
  EmployeesAsigned: {
    label: 'Colaboradores asignados',
    route: '/EmployeesAsigned',
    parent: 'inicio',
  },
}

const asValue = <T>(value: ValueOrFactory<T>, context: BreadcrumbContext): T =>
  typeof value === 'function'
    ? (value as (ctx: BreadcrumbContext) => T)(context)
    : value

const resolveBreadcrumbs = (
  name: string | undefined,
  context: BreadcrumbContext,
  visited: Set<string> = new Set()
): BreadcrumbItem[] => {
  if (!name || visited.has(name)) return []

  const definition = BREADCRUMB_DEFINITIONS[name]
  if (!definition) return []

  visited.add(name)

  const parentNameRaw = definition.parent
    ? asValue(definition.parent, context)
    : undefined

  const parentName =
    typeof parentNameRaw === 'string' && parentNameRaw.length
      ? parentNameRaw
      : undefined

  const parentBreadcrumbs = parentName
    ? resolveBreadcrumbs(parentName, context, visited)
    : []

  const label = asValue(definition.label, context)
  if (!label) {
    return parentBreadcrumbs
  }

  const routeValue = definition.route
    ? asValue(definition.route, context)
    : undefined

  const breadcrumb: BreadcrumbItem = { label }
  if (routeValue) {
    breadcrumb.route = routeValue
  }

  return [...parentBreadcrumbs, breadcrumb]
}

const getBreadcrumbs = (context: BreadcrumbContext): BreadcrumbItem[] => {
  const name = context.to.name as string | undefined
  return resolveBreadcrumbs(name, context)
}

export const breadcrumbGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const breadcrumbStore = useBreadcrumbStore(pinia)
  const breadcrumbs = getBreadcrumbs({ to, from })

  if (breadcrumbs.length) {
    breadcrumbStore.setBreadcrumb(breadcrumbs)
  } else {
    breadcrumbStore.resetBreadcrumb()
  }

  next()
}
